import Image from 'next/image';

import { Box, Flex, Heading, Text } from '@chakra-ui/react';

import { HeadingWithEmphasis } from '~/components/heading-with-emphasis';
import { Section } from '~/components/section';
import { TemplateButton } from '~/components/template-button';
import { TemplateButton as TemplateButtonProps } from '~/features/page-template/models/template-button';

type MentorStepsSectionProps = {
  title: string;
  steps: {
    title: string;
    text: string;
  }[];
  cta: TemplateButtonProps;
};

export function MentorStepsSection({ title, steps, cta }: MentorStepsSectionProps) {
  return (
    <Section
      backgroundColor='accent.sky.100'
      overflow='hidden'
      position='relative'
      padding={{
        base: '40px 0px 0',
        md: '80px 0px 0',
        xl: '80px 160px 0',
      }}
    >
      <Flex flexDirection='column' textAlign='center'>
        {title && (
          <HeadingWithEmphasis
            text={title}
            emphasisProps={{
              color: 'blue.500',
              fontWeight: '500',
            }}
            headingProps={{
              size: 'marketing-5xl',
              as: 'h2',
              marginBottom: '10px',
            }}
          />
        )}

        <Flex
          flexDirection={{ base: 'column', lg: 'row' }}
          gap='32px'
          marginTop='56px'
          flex={{
            lg: '0 0 calc(25% - 20px)',
          }}
        >
          {steps?.map((step, index) => (
            <MentorStepCard key={index} count={index + 1} text={step.text} title={step.title} />
          ))}
        </Flex>

        <Flex height='350px' justifyContent='center' marginTop='48px' position='relative'>
          <Box bottom='0' height='246px' left={{ base: '-70%', lg: '0' }} position='absolute' right='0' width='1120px'>
            <Image
              alt=''
              sizes='1120px'
              src='/images/backgrounds/talent-pipeline-background.svg'
              style={{
                objectPosition: 'center bottom',
                objectFit: 'contain',
              }}
              fill
              priority
            />
          </Box>
          <TemplateButton {...cta} />
        </Flex>
      </Flex>
    </Section>
  );
}

function MentorStepCard({ count, title, text }: { count: number; title: string; text: string }) {
  return (
    <Flex
      alignItems='left'
      bgColor='#171A53'
      borderRadius='8px'
      flexDirection='column'
      padding='24px 24px 40px 24px'
      textAlign='left'
    >
      <Flex>
        <Heading as='h3' color='#BDEA09' fontSize='70px' fontWeight='300'>
          {count}
        </Heading>
      </Flex>
      <Flex flexDirection='column' marginTop='16px'>
        <Heading as='h3' color='white' size='marketing-2xl'>
          {title}
        </Heading>
        <Text color='white' marginTop='8px' maxWidth={{ base: '100%', lg: '250px' }}>
          {text}
        </Text>
      </Flex>
    </Flex>
  );
}
